"use client";

import DangerModal from "@/components/ui/modals/danger-modal";
import { deleteAllCookies } from "@/data/cookieClient";
import { _t } from "@/lib/utils";
import useAppSettings from "@/store/appSettings"; // Zustand state management
import { signOut } from "next-auth/react";
import { useState } from "react";
import { createPortal } from "react-dom";

// Live feedback: the button's own spinner only ever blocked the button
// itself -- everything else on the page (nav, other buttons, links)
// stayed fully clickable during the real async work sign out does
// (the activity log write, then the actual signOut() redirect), so a
// stray click mid-logout could race it. A full screen overlay, portalled
// to document.body so it covers the page regardless of where this
// button lives in the DOM (the topbar's user dropdown, in practice),
// blocks every click underneath it until sign out actually completes.
function SignOutOverlay() {
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-3 bg-black/50 backdrop-blur-sm"
      role="alert"
      aria-live="assertive"
    >
      <span className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
      <p className="text-white text-base font-medium">{_t("logging_out")}</p>
      <p className="text-white/70 text-sm">{_t("redirecting")}</p>
    </div>,
    document.body,
  );
}

// Spec 0015: the one shared teardown both the manual sign out button and the
// footer countdown (on expiry) call, so the two paths can never drift on
// which steps they run. `reason` picks the redirect: "expired" shows the
// existing session_expired feedback message via the app's established
// `/?feedback=<message>` convention (the same one `(dashboard)/layout.tsx`
// already uses for a lost session); "manual" preserves the current path as
// `callbackUrl`, unchanged from the original behavior.
export async function performSignOut(reason: "expired" | "manual" = "manual") {
  // Spec 0021: only the manual path writes the LOGOUT entry here, while the
  // session is still live server side. The "expired" path never reaches
  // this reliably (the session may already be dead by the time this client
  // code runs the instant the countdown hits zero); auth.ts's jwt callback
  // writes that case's LOGOUT entry itself, at the moment it detects the
  // expiry server side, not from a client call.
  if (reason === "manual") {
    try {
      await fetch("/api/activity/logout", { method: "POST" });
    } catch {
      // best effort; never blocks sign out
    }
  }

  deleteAllCookies();
  localStorage.removeItem("app-settings");
  useAppSettings.getState().setUser(null);

  const callbackUrl =
    reason === "expired"
      ? `/?feedback=${encodeURIComponent(_t("session_expired"))}`
      : `/?callbackUrl=${encodeURIComponent(window.location.pathname)}`;

  await signOut({ callbackUrl });
}

interface LogoutButtonProps {
  children?: React.ReactNode;
}

const LogoutButton = ({ children }: LogoutButtonProps) => {
  const [isLoading, setIsLoading] = useState(false); // Track loading state
  const [error, setError] = useState<string | null>(null); // Track error state
  const [isConfirming, setIsConfirming] = useState(false); // Spec 0015, AC-5

  const handleLogout = async () => {
    setIsConfirming(false);
    setIsLoading(true); // Start loading
    setError(null); // Clear any previous errors

    try {
      await performSignOut("manual");
    } catch (error) {
      console.log("Logout failed:", error);
      setError(_t("logout_failed")); // Set error only if logout fails
    } finally {
      setIsLoading(false); // Ensure loading state is cleared
    }
  };

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={(e) => {
          // When this button sits inside a Headless UI Menu (the topbar's
          // user dropdown), activating a MenuItem auto-closes the Menu by
          // default, unmounting this whole component (and its confirm
          // dialog) before the dialog can ever show. preventDefault() is
          // Headless UI's documented escape hatch: it stops that auto-close
          // so the confirmation flow actually gets to run. Harmless when
          // this button isn't inside a Menu (its other call sites).
          e.preventDefault();
          setIsConfirming(true);
        }}
        disabled={isLoading}
        className={`flex items-center gap-2 text-sm text-left ${
          isLoading
            ? "text-gray-400 cursor-not-allowed"
            : "text-gray-400 hover:underline cursor-pointer dark:hover:text-secondary"
        }`}
      >
        {isLoading ? (
          <>
            <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
            {_t("logging_out")}
          </>
        ) : (
          children || _t("sign_out")
        )}
      </button>
      {error && (
        <div
          className="mt-1 text-xs text-secondary"
          role="alert" // Improves accessibility
        >
          {error}
        </div>
      )}
      <DangerModal
        isOpen={isConfirming}
        onCloseAction={() => setIsConfirming(false)}
        onConfirmAction={handleLogout}
        title={_t("sign_out")}
        description={_t("confirm_sign_out_description")}
        confirmText={_t("sign_out")}
        cancelText={_t("cancel")}
      />
      {isLoading && <SignOutOverlay />}
    </div>
  );
};

export default LogoutButton;